import AbstractAnalyticProvider from 'coreClasses/analytics/providers/AbstractAnalyticProvider';
import {EEventType, IEventData} from 'coreClasses/events/IEvents';
import {IAnalyticsEvent} from 'coreClasses/analytics/IAnalytics';
import {isServer} from 'coreUtils/utils';
import GoogleTagManager from 'coreClasses/analytics/providers/gtm/GoogleTagManager';
import {IGoogleTagManagerConfiguration} from 'coreClasses/analytics/providers/gtm/IGoogleTagManager';

import {EAnalyticsEventType} from 'brandClasses/analytics/EAnalyticsEvent';
import ManagerRepository from 'brandClasses/ManagerRepository';

import AnalyticsEventFactory from './AnalyticsEventsFactory';

/**
 * Represents GoogleTagManagerAnalyticsProvider
 */
class GoogleTagManagerAnalyticsProvider extends AbstractAnalyticProvider {
	private eventsQueue: IAnalyticsEvent[] = [];
	private isPageReady: boolean = false;
	private dataLayer: IAnalyticsEvent[] = (window as any).dataLayer;
	private googleTagManager: GoogleTagManager;

	/**
	 * @constructor
	 */
	constructor(managerRepository: ManagerRepository, configuration: IGoogleTagManagerConfiguration) {
		super();

		if (!isServer) {
			const localesMgr = managerRepository.getLocalesMgr();
			this.googleTagManager = new GoogleTagManager(managerRepository, configuration);
			this.dataLayer.push({
				country: localesMgr.getCurrentCountryCode(),
				language: localesMgr.getCurrentLanguageCode()
			});
			this.googleTagManager.loadIntegrationScripts();
			this.loadAdditionalArguments('set', 'url_passthrough', true);
		}
	}

	/**
	 * Tracks analytics event
	 * @param {EEventType} eventType - event type
	 * @param {IEventData} eventData - event data
	 */
	update(eventType: EEventType, eventData: IEventData): void {
		const analyticsEvent = AnalyticsEventFactory.create(eventType, eventData);

		if (analyticsEvent !== null) {
			if (analyticsEvent.event === EAnalyticsEventType.PAGE_VIEW) {
				this.isPageReady = false;

				analyticsEvent.eventReady.then((readyEvent) => {
					const event = {...readyEvent};
					delete event.eventReady;
					this.isPageReady = true;
					this.handleAnalyticsEvent(event);
					this.pushEventsFromQueue();
				});

				return;
			}

			this.pushEventToDataLayer(analyticsEvent);
		}
	}

	/**
	 * Adds event to the DataLayer
	 * @param {IAnalyticsEvent} analyticsEvent - event
	 */
	private pushEventToDataLayer(analyticsEvent: IAnalyticsEvent): void {
		const dataLayer: IAnalyticsEvent[] = (window as any).dataLayer;

		if (!this.isPageReady) {
			this.eventsQueue.push(analyticsEvent);
		} else if (dataLayer) {
			if ('ecommerce' in analyticsEvent) {
				dataLayer.push({
					ecommerce: null
				});
			}

			dataLayer.push(analyticsEvent);
		}
	}

	/**
	 * Pushes all events from the queue to the data layer and clears the queue afterwards.
	 */
	private pushEventsFromQueue() {
		this.eventsQueue.forEach(analyticsEvent => {
			this.pushEventToDataLayer(analyticsEvent);
		});

		this.eventsQueue = [];
	}

	/**
	 * The same event may be emitted a few times, when new data comes.
	 * That's why we should replace the event rather than emitting a new one
	 * @param {IAnalyticsEvent} newEvent - event to replace the old one
	 * @param {number} index - position of event in the list
	 */
	private replaceEventInDataLayer(newEvent: IAnalyticsEvent, index: number) {
		const dataLayer: IAnalyticsEvent[] = (window as any).dataLayer;

		if (dataLayer) {
			dataLayer[index] = newEvent;
		}
	}

	/**
	 * Some events can be emitted a few times, because information about customer, basket etc.
	 * may be loaded later. So we should update an existing event, rather than pushing a new one
	 * @param {IAnalyticsEvent} newEvent - new event
	 */
	private handleAnalyticsEvent(newEvent: IAnalyticsEvent) {
		const dataLayer: IAnalyticsEvent[] = (window as any).dataLayer;

		if (!dataLayer) {
			return;
		}

		let oldEventIndex = -1;

		for (let i = dataLayer.length - 1; i >= 0; i--) {
			if (dataLayer[i].event === newEvent.event) {
				if (dataLayer[i].event === EAnalyticsEventType.PAGE_VIEW) {
					if (dataLayer[i].clean_url === newEvent.clean_url) {
						oldEventIndex = i;
					}

					break;
				}

				if (dataLayer[i].event === EAnalyticsEventType.VIEW_ITEM) {
					if (dataLayer[i].ecommerce.items[0].item_id === newEvent.ecommerce.items[0].item_id) {
						oldEventIndex = i;
					}

					break;
				}
			}
		}

		if (oldEventIndex > -1) {
			this.replaceEventInDataLayer(newEvent, oldEventIndex);
		} else {
			this.pushEventToDataLayer(newEvent);
		}
	}

	/**
	 * Load additional arguments to data layer
	 */
	protected loadAdditionalArguments(){
		this.dataLayer.push(arguments);
	}
}

export default GoogleTagManagerAnalyticsProvider;
